import { useForm } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "../../components/Button";
import { Form } from "../../components/Form";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Input } from "../../components/Input";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { SelectInput } from "../../components/SelectInput";
import { Toggle } from "../../components/Toggle";
import { formatDate } from "../../utils/dateUtils";
import type { DeviceConfigId } from "@KassaroAB/shared/types/ids";
import type { RestDeviceConfig } from "@KassaroAB/shared/types/deviceConfig";


const ReadOnlyField = styled.div`
  padding: ${spacing.x2}px;
  background-color: ${colors.backgroundDark};
  border-radius: 4px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  font-family: monospace;
`;

interface DeviceFormProps {
  device: {
    id: string;
    name: string | null;
    serialNumber?: string | null;
    platform: string | null;
    osVersion: string | null;
    appVersion: string | null;
    isActive: boolean;
    deviceConfigId: DeviceConfigId | null;
    lastSeenAt: string;
    createdAt: string;
  };
  deviceConfigs?: RestDeviceConfig[];
  onSubmit: (data: {
    name: string | null;
    serialNumber: string | null;
    isActive: boolean;
    deviceConfigId: DeviceConfigId | null;
  }) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const DeviceForm = ({
  device,
  deviceConfigs = [],
  onSubmit,
  onCancel,
  isLoading,
}: DeviceFormProps) => {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: {
      name: device.name ?? "",
      serialNumber: device.serialNumber ?? "",
      isActive: device.isActive,
      deviceConfigId: device.deviceConfigId ?? "",
    },
    onSubmit: ({ value }) => {
      onSubmit({
        name: value.name || null,
        serialNumber: value.serialNumber || null,
        isActive: value.isActive,
        deviceConfigId: (value.deviceConfigId || null) as DeviceConfigId | null,
      });
    },
  });

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormGroup>
        <FormLabel>{t("deviceForm.platform")}</FormLabel>
        <ReadOnlyField>{device.platform || "-"}</ReadOnlyField>
      </FormGroup>

      <FormGroup>
        <FormLabel>{t("deviceForm.osVersion")}</FormLabel>
        <ReadOnlyField>{device.osVersion || "-"}</ReadOnlyField>
      </FormGroup>

      <FormGroup>
        <FormLabel>{t("deviceForm.appVersion")}</FormLabel>
        <ReadOnlyField>{device.appVersion || "-"}</ReadOnlyField>
      </FormGroup>

      <form.Field name="name">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("deviceForm.name")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t("deviceForm.namePlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="serialNumber">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("deviceForm.serialNumber")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t("deviceForm.serialNumberPlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="isActive">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("deviceForm.active")}</FormLabel>
            <Toggle
              checked={field.state.value}
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              label={field.state.value ? t("deviceForm.activeState") : t("deviceForm.inactiveState")}
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="deviceConfigId">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("deviceForm.deviceConfig")}</FormLabel>
            <SelectInput
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value || "")}
              onBlur={field.handleBlur}
            >
              <option value="">{t("deviceForm.none")}</option>
              {deviceConfigs.map((config) => (
                <option key={config.id} value={config.id}>
                  {config.name}
                </option>
              ))}
            </SelectInput>
          </FormGroup>
        )}
      </form.Field>

      <FormGroup>
        <FormLabel>{t("deviceForm.lastSeen")}</FormLabel>
        <ReadOnlyField>
          {formatDate(device.lastSeenAt)}
        </ReadOnlyField>
      </FormGroup>

      <FormGroup>
        <FormLabel>{t("deviceForm.created")}</FormLabel>
        <ReadOnlyField>
          {formatDate(device.createdAt)}
        </ReadOnlyField>
      </FormGroup>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel} disabled={isLoading}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t("common.saving") : t("common.saveChanges")}
        </Button>
      </FormActions>
    </Form>
  );
};
